import { useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import type { CardPaymentFormFields } from '@cencosud-cencommerce/paris-uikit';
import {
  CardPayment,
  CardType,
  Checkbox,
  Select,
  TermsLinksAndLabel,
  TextField
} from '@cencosud-cencommerce/paris-uikit';

import { useAppDispatch, useAppSelector } from '@/Application/Hooks/Redux';
import { selectBuyNowSelectedCard } from '@/Application/Selectors/buyNow/useBuyNowCart';
import { selectBuyNowPaymentMethodsData } from '@/Application/Selectors/buyNow/useBuyNowPaymentMethods';
import { setSelectedCard } from '@/modules/buy-now/infrastructure/slices';
import { BuyNowEventInteractiveTags } from '@/modules/shared/utils/gtmEvents/buyNowTypes';
import { sendEvent } from '@/modules/shared/utils/gtmEvents/sendEvents/sendBuyNowEvents';
import { usePaymentButton } from '@/Pages/BuyNowPage/hooks/usePaymentButton';
import PickCardModal from '@/Pages/BuyNowPage/PaymentAndShipping/partials/PickCard/PickCardModal';
import CardPaymentModal from '@/Pages/BuyNowPage/PaymentAndShipping/partials/SavedCardPayment/CardPaymentModal';
import {
  findFavoriteAvailableCard,
  mapBrandToCardType
} from '@/Pages/BuyNowPage/PaymentAndShipping/partials/SavedCardPayment/utils';

export const SavedCardPayment = () => {
  const dispatch = useAppDispatch();
  const selectedCard = useAppSelector(selectBuyNowSelectedCard);
  const paymentMethodsData = useAppSelector(selectBuyNowPaymentMethodsData);

  const { isTermsChecked, submissionHandler } = usePaymentButton();
  const { control } = useFormContext<CardPaymentFormFields>();

  const [modalOpen, setModalOpen] = useState<
    null | 'address' | 'cardPayment' | 'pickCard'
  >(null);

  const sortedSavedCards = useMemo(() => {
    const cards = paymentMethodsData?.savedCards || [];
    if (!cards.length) return [];
    return [...cards].sort((a, b) => {
      if (a?.isFavorite && !b?.isFavorite) return -1;
      if (!a?.isFavorite && b?.isFavorite) return 1;
      return 0;
    });
  }, [paymentMethodsData?.savedCards]);

  const cencoFees = paymentMethodsData?.cencoFees || [];
  const creditFees = paymentMethodsData?.creditFees || [];

  const getFeesOptions = (cardType: CardType) => {
    const fees = cardType === CardType?.CENCOSUD ? cencoFees : creditFees;
    return fees.map((fee) => ({
      label: `${fee?.fee} cuota${fee.fee > 1 ? 's' : ''}`,
      value: fee?.fee.toString()
    }));
  };

  useEffect(() => {
    if (sortedSavedCards?.length > 0 && !selectedCard) {
      const bestCard = findFavoriteAvailableCard(sortedSavedCards);
      dispatch(
        setSelectedCard({
          cardLastFourDigits: bestCard?.lastDigits,
          type:
            bestCard?.methodId === 'EPAY_CENCOSUD_TCOM'
              ? CardType.CENCOSUD
              : mapBrandToCardType(bestCard?.brand),
          token: bestCard?.token
        })
      );
    }
  }, [sortedSavedCards, selectedCard, dispatch]);

  return (
    <>
      <CardPayment
        buttonPay={{
          onClick: () => {
            sendEvent({
              event: 'baseEvent',
              extraFields: {
                'event-interactive-tag': BuyNowEventInteractiveTags.PayButton
              }
            });
            submissionHandler();
          },
          disabled: !isTermsChecked
        }}
        cardLastFourDigits={selectedCard?.cardLastFourDigits ?? '****'}
        type={selectedCard?.type ?? CardType?.GENERIC}
        title="Tarjetas guardadas"
        promotext="Ahorro $35.990"
        primaryActionLink={
          sortedSavedCards.length > 1 || !selectedCard
            ? {
                actionText: 'Elegir otra tarjeta',
                onClick: () => {
                  setModalOpen('pickCard');
                  sendEvent({
                    event: 'baseEvent',
                    extraFields: {
                      'event-interactive-tag':
                        BuyNowEventInteractiveTags.ElegirOtraTarjeta
                    }
                  });
                }
              }
            : undefined
        }
        secondaryActionLink={{
          actionText: 'Otros medios de pago',
          onClick: () => {
            setModalOpen('cardPayment');
            sendEvent({
              event: 'baseEvent',
              extraFields: {
                'event-interactive-tag':
                  BuyNowEventInteractiveTags.OtherPaymentMethods
              }
            });
          }
        }}
        cvvInputElement={
          <Controller
            name="cvv"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                className="ui-w-full"
                data-testid="card-payment-cvv-input"
                label="CVV"
                placeholder="CVV"
                type="password"
                autoComplete="cc-csc"
                helperText={fieldState.error?.message}
                status={fieldState?.error ? 'error' : 'neutral'}
                inputMode="numeric"
                maxLength={4}
                {...field}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  field.onChange(value.slice(0, 4));
                }}
              />
            )}
          />
        }
        termsCheckboxElement={
          <Controller
            name="terms"
            control={control}
            render={({ field }) => (
              <Checkbox
                data-testid="card-payment-terms-checkbox"
                id="terms"
                label={<TermsLinksAndLabel />}
                checked={!!field.value}
                disabled={field.disabled}
                onChange={(e) => field.onChange(e.target.checked)}
                onBlur={field.onBlur}
                name={field?.name}
              />
            )}
          />
        }
        feesSelectElement={
          <Controller
            name="fees"
            control={control}
            render={({ field, fieldState }) => (
              <Select
                {...field}
                className="ui-w-full"
                data-testid="card-payment-fees-select"
                label="Número de cuotas"
                options={selectedCard ? getFeesOptions(selectedCard.type) : []}
                placeholder="1 cuota"
                helperText={fieldState?.error?.message}
                status={fieldState?.error ? 'error' : 'neutral'}
              />
            )}
          />
        }
      />
      <CardPaymentModal
        isOpen={modalOpen === 'cardPayment'}
        onClose={() => setModalOpen(null)}
      />
      <PickCardModal
        isOpen={modalOpen === 'pickCard'}
        onClose={() => {
          setModalOpen(null);
        }}
        savedCards={sortedSavedCards}
        currentSelectedToken={selectedCard?.token ?? null}
        onSelectCard={(card) => {
          dispatch(
            setSelectedCard({
              cardLastFourDigits: card?.lastDigits,
              type:
                card?.methodId === 'EPAY_CENCOSUD_TCOM'
                  ? CardType.CENCOSUD
                  : mapBrandToCardType(card?.brand),
              token: card?.token
            })
          );
          setModalOpen(null);
        }}
      />
    </>
  );
};
